import React, { useEffect, useState } from 'react';
import { walletAPI, adminAPI } from '@/services/api';
import { adminCandidatesAPI } from '@/services/staffingAPI';
import { Coins, Search, Plus, Minus, Download, Settings2, AlertCircle, Crown, Users } from 'lucide-react';
import Pagination, { usePagination } from '@/components/shared/Pagination';
import ScrollHint from '@/components/shared/ScrollHint';

const BUCKET_LABELS = {
  hiring_heads: 'Hiring Org Heads',
  hiring_subs: 'Hiring Org Subs',
  staffing_heads: 'Staffing Org Heads',
  staffing_subs: 'Staffing Org Subs',
  candidates_individual: 'Individual Candidates',
  candidates_staffing: 'Staffing-Managed Candidates',
  admins: 'Admins',
};

function BreakdownPanel() {
  const [data, setData] = useState(null);
  useEffect(() => { adminCandidatesAPI.walletBreakdown().then(setData).catch(() => { /* noop */ }); }, []);
  if (!data) return null;
  return (
    <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-xl p-5 mb-5" data-testid="wallet-breakdown">
      <p className="text-slate-900 dark:text-white text-sm font-medium mb-3">MP Distribution by Entity Type</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(data.buckets || {}).map(([key, b]) => (
          <div key={key} className="bg-slate-50 dark:bg-[#0a0a0b] rounded-lg p-3 border border-slate-200 dark:border-zinc-800/50" data-testid={`bucket-${key}`}>
            <p className="text-slate-500 dark:text-zinc-500 text-[11px] uppercase tracking-wider truncate">{BUCKET_LABELS[key] || key}</p>
            <p className="text-slate-900 dark:text-white text-lg font-semibold mt-1">{b.total_balance.toLocaleString()} <span className="text-xs text-slate-500 dark:text-zinc-500">MP</span></p>
            <p className="text-slate-500 dark:text-zinc-500 text-xs">{b.count} user{b.count === 1 ? '' : 's'}</p>
          </div>
        ))}
      </div>
      <p className="text-slate-500 dark:text-zinc-600 text-xs mt-3 text-right">Total: {data.total_balance.toLocaleString()} MP across {data.total_users} users</p>
    </div>
  );
}

function ApplicantWalletsSection({ onError }) {
  const [applicants, setApplicants] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [pending, setPending] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [grant, setGrant] = useState({ open: false, user: null, amount: '', reason: '' });
  const pageSize = 20;

  const load = async (searchQ, p) => {
    setLoading(true);
    try {
      const r = await walletAPI.adminListApplicantWallets({ q: searchQ, skip: (p - 1) * pageSize, limit: pageSize });
      setApplicants(r.applicants || []);
      setTotal(r.total || 0);
    } catch (e) {
      onError?.(e.response?.data?.detail || e.message);
    }
    setLoading(false);
  };

  useEffect(() => { load('', 1); }, []);

  const applySearch = (e) => {
    e.preventDefault();
    setQ(pending);
    setPage(1);
    load(pending, 1);
  };

  const goPage = (p) => {
    setPage(p);
    load(q, p);
  };

  const submitGrant = async () => {
    const n = parseInt(grant.amount, 10);
    if (!n || n === 0) return;
    try {
      await walletAPI.adminCredit(grant.user.user_id, n, grant.reason || (n > 0 ? 'Admin grant · applicant' : 'Admin revoke · applicant'));
      window.dispatchEvent(new Event('mu-wallet-changed'));
      setGrant({ open: false, user: null, amount: '', reason: '' });
      load(q, page);
    } catch (e) {
      onError?.(e.response?.data?.detail || e.message);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <section className="mt-8" data-testid="admin-applicant-wallets-section">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <h2 className="text-sm uppercase tracking-wider text-slate-600 dark:text-zinc-300 flex items-center gap-2">
          <Users className="w-4 h-4 text-indigo-500" /> Applicant wallets
        </h2>
        <p className="text-xs text-slate-500 dark:text-zinc-500">Offer or revoke Mu points for any candidate</p>
      </div>

      <form onSubmit={applySearch} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-3 flex flex-wrap gap-2 mb-3">
        <div className="flex-1 min-w-[220px] relative">
          <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-slate-500 dark:text-zinc-500" />
          <input
            value={pending}
            onChange={e => setPending(e.target.value)}
            placeholder="Search applicant by email…"
            className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded pl-8 pr-3 py-1.5 text-sm text-slate-900 dark:text-white"
            data-testid="applicant-wallet-search-input"
          />
        </div>
        <button type="submit" className="px-3 py-1 bg-slate-900 dark:bg-white text-white dark:text-black rounded text-sm font-medium" data-testid="apply-applicant-wallet-search">
          Apply
        </button>
      </form>

      {loading ? (
        <p className="text-slate-500 dark:text-zinc-500 text-sm">Loading…</p>
      ) : applicants.length === 0 ? (
        <p className="text-slate-500 dark:text-zinc-500 text-sm py-8 text-center bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg" data-testid="admin-applicants-empty">
          No applicants found{q ? ` for "${q}"` : ''}.
        </p>
      ) : (
        <ScrollHint>
          <div className="overflow-x-auto bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg">
            <table className="w-full text-xs min-w-[720px]" data-testid="admin-applicants-table">
              <thead className="border-b border-slate-200 dark:border-zinc-800/70 text-slate-500 dark:text-zinc-500 text-left">
                <tr>
                  <th className="px-3 py-2 font-medium">Applicant</th>
                  <th className="px-3 py-2 font-medium">Phone</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium text-right">Balance</th>
                  <th className="px-3 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {applicants.map(a => (
                  <tr key={a.user_id} className="border-b border-slate-200 dark:border-zinc-900/60" data-testid={`admin-applicant-wallet-${a.user_id}`}>
                    <td className="px-3 py-2">
                      <span className="text-slate-900 dark:text-white">{a.email || '—'}</span>
                      {a.name && <p className="text-slate-500 dark:text-zinc-500 text-[10px]">{a.name}</p>}
                    </td>
                    <td className="px-3 py-2 text-slate-600 dark:text-zinc-300">{a.phone || '—'}</td>
                    <td className="px-3 py-2 text-slate-500 dark:text-zinc-400">{a.is_disabled ? 'disabled' : 'active'}</td>
                    <td className={`px-3 py-2 text-right font-medium ${a.balance < 0 ? 'text-red-600 dark:text-red-400' : a.balance < 20 ? 'text-amber-700 dark:text-amber-300' : 'text-emerald-700 dark:text-emerald-300'}`}>
                      {a.balance} MP
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-1.5">
                        <button
                          onClick={() => setGrant({ open: true, user: a, amount: '10', reason: '' })}
                          className="px-2 py-1 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20 rounded text-[11px] inline-flex items-center gap-1"
                          data-testid={`applicant-grant-${a.user_id}`}
                        >
                          <Plus className="w-3 h-3" /> Offer
                        </button>
                        <button
                          onClick={() => setGrant({ open: true, user: a, amount: '-10', reason: '' })}
                          className="px-2 py-1 bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-500/20 rounded text-[11px] inline-flex items-center gap-1"
                          data-testid={`applicant-revoke-${a.user_id}`}
                        >
                          <Minus className="w-3 h-3" /> Revoke
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ScrollHint>
      )}

      {total > pageSize && (
        <div className="flex items-center justify-between mt-3 text-xs text-slate-600 dark:text-zinc-300" data-testid="admin-applicant-wallets-pg">
          <p>Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}</p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => goPage(Math.max(1, page - 1))}
              disabled={page <= 1}
              className="px-2 py-1 rounded border border-slate-200 dark:border-zinc-800 disabled:opacity-40"
              data-testid="admin-applicant-pg-prev"
            >
              Prev
            </button>
            <span data-testid="admin-applicant-pg-current">Page {page} / {totalPages}</span>
            <button
              onClick={() => goPage(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages}
              className="px-2 py-1 rounded border border-slate-200 dark:border-zinc-800 disabled:opacity-40"
              data-testid="admin-applicant-pg-next"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {grant.open && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setGrant({ open: false, user: null, amount: '', reason: '' })} data-testid="applicant-grant-modal">
          <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
            <h3 className="text-slate-900 dark:text-white font-semibold mb-1">
              {parseInt(grant.amount, 10) >= 0 ? 'Offer' : 'Revoke'} Mu Points
            </h3>
            <p className="text-slate-500 dark:text-zinc-500 text-xs mb-4">
              {grant.user.email} · current balance <span className="text-amber-700 dark:text-amber-300">{grant.user.balance} MP</span>
            </p>
            <input
              type="number"
              placeholder="Amount (negative to revoke)"
              value={grant.amount}
              onChange={e => setGrant(s => ({ ...s, amount: e.target.value }))}
              className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white mb-2"
              data-testid="applicant-grant-amount-input"
              autoFocus
            />
            <input
              placeholder="Reason (optional)"
              value={grant.reason}
              onChange={e => setGrant(s => ({ ...s, reason: e.target.value }))}
              className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white mb-3"
              data-testid="applicant-grant-reason-input"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setGrant({ open: false, user: null, amount: '', reason: '' })}
                className="flex-1 py-2 bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 rounded-lg text-sm"
              >
                Cancel
              </button>
              <button
                onClick={submitGrant}
                className="flex-1 py-2 bg-slate-900 dark:bg-white text-white dark:text-black rounded-lg text-sm font-medium"
                data-testid="confirm-applicant-grant-btn"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}


export default function AdminWallets() {
  const [wallets, setWallets] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [filter, setFilter] = useState({ q: '', org_id: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [grant, setGrant] = useState({ open: false, user: null, amount: '', reason: '' });
  const [pricingEditor, setPricingEditor] = useState({ open: false, org: null, values: {} });
  const pg = usePagination(wallets, 20);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await walletAPI.adminListWallets(filter);
      setWallets(r.wallets || []);
      if (!orgs.length) {
        try {
          const h = await adminAPI.getOrgHierarchy();
          setOrgs(h.organizations || []);
        } catch {/* ignore */}
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
    setLoading(false);
  };

  useEffect(() => { load();}, []);

  const apply = (e) => { e.preventDefault(); load(); };

  const grantSubmit = async () => {
    const n = parseInt(grant.amount, 10);
    if (!n || n === 0) return;
    try {
      await walletAPI.adminCredit(grant.user.user_id, n, grant.reason || (n > 0 ? 'Admin grant' : 'Admin revoke'));
      window.dispatchEvent(new Event('mu-wallet-changed'));
      setGrant({ open: false, user: null, amount: '', reason: '' });
      load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const openPricing = async (org) => {
    try {
      const r = await walletAPI.adminGetPricing(org.id);
      setPricingEditor({ open: true, org, values: r.pricing });
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const savePricing = async () => {
    try {
      const sanitized = Object.fromEntries(Object.entries(pricingEditor.values).map(([k, v]) => [k, parseInt(v, 10) || 0]));
      await walletAPI.adminSetPricing(pricingEditor.org.id, sanitized);
      setPricingEditor({ open: false, org: null, values: {} });
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  const dl = async () => { try { await walletAPI.adminDownloadCsv({ org_id: filter.org_id }); } catch (e) { setError(e.response?.data?.detail || e.message); } };

  return (
    <div data-testid="admin-wallets">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white tracking-tight flex items-center gap-2"><Coins className="w-5 h-5 text-amber-600 dark:text-amber-400" /> Mu Points Admin</h1>
          <p className="text-slate-500 dark:text-zinc-500 text-sm">Grant or revoke MP for any recruiter. Configure per-org pricing.</p>
        </div>
        <button onClick={dl} className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 hover:bg-slate-200 dark:hover:bg-zinc-700 rounded-lg text-sm" data-testid="admin-dl-csv-btn"><Download className="w-4 h-4" /> Download CSV</button>
      </div>

      {error && <div className="mb-4 px-4 py-2 rounded bg-red-500/10 text-red-600 dark:text-red-400 text-sm flex items-center gap-2"><AlertCircle className="w-4 h-4" />{error}</div>}

      <BreakdownPanel />

      {/* Pricing per org */}
      <section className="mb-6" data-testid="admin-pricing-section">
        <h2 className="text-sm uppercase tracking-wider text-slate-600 dark:text-zinc-300 mb-2">Per-organisation pricing</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {orgs.map(o => (
            <div key={o.id} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-3 flex items-center justify-between" data-testid={`org-pricing-${o.id}`}>
              <div className="min-w-0">
                <p className="text-slate-900 dark:text-white text-sm font-medium truncate">{o.name}</p>
                <p className="text-slate-500 dark:text-zinc-500 text-xs">@{o.domain}</p>
              </div>
              <button onClick={() => openPricing(o)} className="px-3 py-1.5 bg-slate-100 dark:bg-zinc-800/80 hover:bg-slate-100 dark:hover:bg-zinc-800 text-slate-600 dark:text-zinc-300 rounded-lg text-xs flex items-center gap-1.5" data-testid={`edit-pricing-${o.id}`}><Settings2 className="w-3 h-3" /> Pricing</button>
            </div>
          ))}
        </div>
      </section>

      {/* Filter */}
      <form onSubmit={apply} className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg p-3 flex flex-wrap gap-2 mb-4">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-slate-500 dark:text-zinc-500" />
          <input value={filter.q} onChange={e => setFilter({ ...filter, q: e.target.value })} placeholder="Search by email…" className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded pl-8 pr-3 py-1.5 text-sm text-slate-900 dark:text-white" data-testid="wallet-search-input" />
        </div>
        <select value={filter.org_id} onChange={e => setFilter({ ...filter, org_id: e.target.value })} className="bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded px-3 py-1.5 text-sm text-slate-900 dark:text-white" data-testid="wallet-org-filter">
          <option value="">All organisations</option>
          {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <button type="submit" className="px-3 py-1 bg-slate-900 dark:bg-white text-white dark:text-black rounded text-sm font-medium" data-testid="apply-wallet-filters">Apply</button>
      </form>

      {/* Wallets list */}
      {loading ? <p className="text-slate-500 dark:text-zinc-500 text-sm">Loading…</p> : (
        <ScrollHint>
        <div className="overflow-x-auto bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800/50 rounded-lg">
          <table className="w-full text-xs min-w-[720px]">
            <thead className="border-b border-slate-200 dark:border-zinc-800/70 text-slate-500 dark:text-zinc-500 text-left">
              <tr>
                <th className="px-3 py-2 font-medium">Recruiter</th>
                <th className="px-3 py-2 font-medium">Organisation</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium text-right">Balance</th>
                <th className="px-3 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pg.pageItems.map(w => (
                <tr key={w.user_id} className="border-b border-slate-200 dark:border-zinc-900/60" data-testid={`admin-wallet-${w.user_id}`}>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      {w.is_head && <Crown className="w-3 h-3 text-amber-600 dark:text-amber-400" />}
                      <span className="text-slate-900 dark:text-white">{w.email}</span>
                    </div>
                    {w.name && <p className="text-slate-500 dark:text-zinc-500 text-[10px]">{w.name}</p>}
                  </td>
                  <td className="px-3 py-2 text-slate-600 dark:text-zinc-300">{w.organization_name || '—'} <span className="text-slate-500 dark:text-zinc-600">@{w.domain || '—'}</span></td>
                  <td className="px-3 py-2 text-slate-500 dark:text-zinc-400">{w.member_status}</td>
                  <td className={`px-3 py-2 text-right font-medium ${w.balance < 0 ? 'text-red-600 dark:text-red-400' : w.balance < 20 ? 'text-amber-700 dark:text-amber-300' : 'text-emerald-700 dark:text-emerald-300'}`}>{w.balance} MP</td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-1.5">
                      <button onClick={() => setGrant({ open: true, user: w, amount: '50', reason: '' })} className="px-2 py-1 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20 rounded text-[11px] inline-flex items-center gap-1" data-testid={`grant-${w.user_id}`}><Plus className="w-3 h-3" /> Grant</button>
                      <button onClick={() => setGrant({ open: true, user: w, amount: '-50', reason: '' })} className="px-2 py-1 bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-500/20 rounded text-[11px] inline-flex items-center gap-1" data-testid={`revoke-${w.user_id}`}><Minus className="w-3 h-3" /> Revoke</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </ScrollHint>
      )}

      <Pagination {...pg.paginationProps} testIdPrefix="admin-wallets-pg" />

      {/* Applicant wallets — grant/revoke Mu to individual candidates */}
      <ApplicantWalletsSection onError={setError} />

      {/* Grant modal */}
      {grant.open && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setGrant({ open: false, user: null, amount: '', reason: '' })} data-testid="grant-modal">
          <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
            <h3 className="text-slate-900 dark:text-white font-semibold mb-1">{parseInt(grant.amount, 10) >= 0 ? 'Grant' : 'Revoke'} Mu Points</h3>
            <p className="text-slate-500 dark:text-zinc-500 text-xs mb-4">{grant.user.email} · current balance <span className="text-amber-700 dark:text-amber-300">{grant.user.balance} MP</span></p>
            <input type="number" placeholder="Amount (negative to revoke)" value={grant.amount} onChange={e => setGrant(s => ({ ...s, amount: e.target.value }))} className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white mb-2" data-testid="grant-amount-input" autoFocus />
            <input placeholder="Reason (optional)" value={grant.reason} onChange={e => setGrant(s => ({ ...s, reason: e.target.value }))} className="w-full bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white mb-3" data-testid="grant-reason-input" />
            <div className="flex gap-3">
              <button onClick={() => setGrant({ open: false, user: null, amount: '', reason: '' })} className="flex-1 py-2 bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 rounded-lg text-sm">Cancel</button>
              <button onClick={grantSubmit} className="flex-1 py-2 bg-slate-900 dark:bg-white text-white dark:text-black rounded-lg text-sm font-medium" data-testid="confirm-grant-btn">Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* Pricing editor */}
      {pricingEditor.open && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setPricingEditor({ open: false, org: null, values: {} })} data-testid="pricing-editor-modal">
          <div className="bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
            <h3 className="text-slate-900 dark:text-white font-semibold mb-1">Pricing · {pricingEditor.org.name}</h3>
            <p className="text-slate-500 dark:text-zinc-500 text-xs mb-4">All values in Mu Points</p>
            {Object.entries(pricingEditor.values).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between gap-3 mb-2">
                <label className="text-slate-600 dark:text-zinc-300 text-sm capitalize">{k.replace(/_/g, ' ')}</label>
                <input type="number" min="0" value={v} onChange={e => setPricingEditor(s => ({ ...s, values: { ...s.values, [k]: e.target.value } }))} className="w-24 bg-slate-50 dark:bg-[#0a0a0b] border border-slate-200 dark:border-zinc-800 rounded px-2 py-1 text-sm text-slate-900 dark:text-white text-right" data-testid={`pricing-input-${k}`} />
              </div>
            ))}
            <div className="flex gap-3 mt-4">
              <button onClick={() => setPricingEditor({ open: false, org: null, values: {} })} className="flex-1 py-2 bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 rounded-lg text-sm">Cancel</button>
              <button onClick={savePricing} className="flex-1 py-2 bg-slate-900 dark:bg-white text-white dark:text-black rounded-lg text-sm font-medium" data-testid="save-pricing-btn">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
